import { useCallback, useEffect, useRef, useState } from "react";
import { GD } from "src/GD";

export const useAPI = <T,>(initialRequest: IAPIRequest | null, dependency?: readonly unknown[]) => {
	const [response, setResponse] = useState<IAPIResponse<T> | null>(null);
	const [busy, setBusy] = useState(false);
	const request = useRef(initialRequest);
	const current = useRef(0);
	const controller = useRef<AbortController | null>(null);
	const mounted = useRef(true);
	const cancel = useCallback(() => {
		current.current++;
		controller.current?.abort();
		controller.current = null;
	}, []);

	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
			cancel();
		};
	}, [cancel]);

	const executeRequest = useCallback(async (next?: IAPIRequest | null) => {
		if (next) request.current = next;
		const req = next ?? request.current;
		if (!req || !mounted.current) return;

		cancel();
		const revision = current.current;
		const pending = new AbortController();
		controller.current = pending;
		const abort = () => pending.abort(req.signal?.reason);
		if (req.signal?.aborted) abort();
		else req.signal?.addEventListener("abort", abort, { once: true });
		setBusy(true);
		try {
			if (pending.signal.aborted) return;
			const result = await GD.REQ_API.request({ ...req, signal: pending.signal }) as IAPIResponse<T>;
			if (mounted.current && revision === current.current && !pending.signal.aborted) setResponse(result);
			return result;
		} catch (cause) {
			const result: IAPIResponse<T> = { error: { code: -1, message: cause instanceof Error ? cause.message : String(cause) } };
			if (mounted.current && revision === current.current && !pending.signal.aborted) setResponse(result);
			return result;
		} finally {
			req.signal?.removeEventListener("abort", abort);
			if (mounted.current && revision === current.current) {
				controller.current = null;
				setBusy(false);
			}
		}
	}, [cancel]);

	useEffect(() => {
		void executeRequest();
		// The optional dependency list explicitly controls automatic reloads. Request
		// objects are often created inline; their identity must not trigger a fetch.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [executeRequest, ...(dependency ?? [])]);

	const resetData = useCallback(() => {
		cancel();
		request.current = null;
		setResponse(null);
		setBusy(false);
	}, [cancel]);

	return [response, executeRequest, busy, resetData] as const;
};
